import { Component } from '@theme/component';

const POLL_MS = 100;
const MAX_ATTEMPTS = 50; // ~5s

/**
 * A custom element that displays a wishlist button.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} wishlistAppButton - The wishlist app button element.
 * @property {HTMLButtonElement} wishlistFakeButton - The wishlist fake button element.
 *
 * @extends {Component<Refs>}
 */
export class WishlistAddButtonComponent extends Component {
	requiredRefs = ['wishlistAppButton', 'wishlistFakeButton'];

	/** @type {ReturnType<typeof setInterval> | null} */
	#readyInterval = null;

	/** @type {boolean} */
	#isBound = false;

	/** @type {MutationObserver | null} */
	#observer = null;

	/** @type {(() => void) | null} */
	#updateAllButtonsCallback = null;

	connectedCallback() {
		super.connectedCallback();

		const { wishlistAppButton, wishlistFakeButton } = this.refs;
		this.#observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (!['aria-pressed', 'aria-label'].includes(mutation.attributeName ?? '')) return;
				
				const node = /** @type {HTMLButtonElement} */ (mutation.target);

				wishlistFakeButton.setAttribute(
					mutation.attributeName ?? '',
					String(node.getAttribute(mutation.attributeName ?? ''))
				);
			});
		});

		this.#observer.observe(wishlistAppButton, { attributes: true });

		if (window.wishlistApp) {
			this.#onWishlistReady();
			return;
		}

		/* Poll wishlistApp — the app exposes no ready event/hook */
		let attempts = 0;
		this.#readyInterval = setInterval(() => {
			attempts++;

			if (window.wishlistApp) {
				this.#clearReadyInterval();
				this.#onWishlistReady();
			} else if (attempts >= MAX_ATTEMPTS) {
				this.#clearReadyInterval();
			}
		}, POLL_MS);
	}

	disconnectedCallback() {
		this.#clearReadyInterval();
		this.#observer?.disconnect();
		this.#observer = null;

		if (this.#isBound) {
			if (this.#updateAllButtonsCallback != null) {
				window.removeEventListener('wishlist:updated', this.#updateAllButtonsCallback);
				window.removeEventListener('load', this.#updateAllButtonsCallback);
				this.#updateAllButtonsCallback = null;
			}
			this.refs.wishlistFakeButton?.removeEventListener('click', this.delegateWishlistButtonClick);
			this.#isBound = false;
		}

		super.disconnectedCallback();
	}

	/**
	 * Clears the polling interval.
	 */
	#clearReadyInterval() {
		if (this.#readyInterval == null) return;
		clearInterval(this.#readyInterval);
		this.#readyInterval = null;
	}

	/**
	 * Called when the wishlist app is ready.
	 */
	#onWishlistReady() {
		if (this.#isBound) return;
		this.#isBound = true;
		this.#updateAllButtonsCallback = () => {
			window.wishlistApp.updateAllButtons();
		};
		window.addEventListener('wishlist:updated', this.#updateAllButtonsCallback);
		if (document.readyState === 'complete') {
			this.#updateAllButtonsCallback();
		} else {
			window.addEventListener('load', this.#updateAllButtonsCallback, { once: true });
		}
		this.refs.wishlistFakeButton.addEventListener('click', this.delegateWishlistButtonClick);
	}

	/**
	 * Delegates the wishlist add button click to the wishlist app add button.
	 * @param {Event} event - The event that triggered the navigation.
	 */
	delegateWishlistButtonClick = (event) => {
		event.preventDefault();
		event.stopPropagation();

		const { wishlistAppButton } = this.refs;

		wishlistAppButton.click();
	};
}

if (!customElements.get('wishlist-add-button')) {
	customElements.define('wishlist-add-button', WishlistAddButtonComponent);
}
